/**
 * @file PostACase.jsx
 * @description Focused conversion page at /post-a-case for agencies arriving from
 *   cold-outreach emails. Deliberately short: repeat the email's offer (first cases
 *   free through the promo end date, no card), how it works in 3 steps, that it works
 *   alongside Indeed/an ATS, and trust markers. One primary CTA, repeated.
 *
 *   Not an SEO page: noindex and kept out of the sitemap, but PRERENDERED (seoRoutes.json,
 *   sitemap:false) so the first paint is this agency page, not the provider home shell
 *   (/post-aba-cases stays the SEO page). No fake numbers, testimonials, or competitor names other than Indeed.
 *   Early-stage positioning: no provider-supply numbers anywhere; the hero chips are
 *   non-numeric differentiators. Founding Partner applications open the contact modal.
 * @module components/PostACase
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useConfigVersion } from "../lib/useConfigVersion";
import { T } from "../theme";
import { BriefcaseIcon, BellIcon, UsersIcon, ShieldIcon, LockIcon, CheckIcon, BuildingIcon } from "./Icons";
import ContactModal from "./ContactModal";
import PublicFooter from "./PublicFooter";
import StickyMobileCta from "./StickyMobileCta";
import { ProofChips, FlowSteps, IconTiles, CasePreview, DifferentialGrid, PartnerCard, FounderCard, OutsideFloridaNote, agencyChips } from "./AgencyVisuals";
import CasePromotion from "./CasePromotion";
import { setSignupIntent } from "../lib/signupIntent";
import { usePageSeo } from "../lib/seo";
import { track, A, ctaMeta, landingMeta, trackLandingView } from "../lib/analytics";
import { getStarterPromoDates } from "../lib/plans";
import { getAgencyPricing } from "../lib/agencyPricing";

const NAVY = "#0F1024";
const NAVY_MID = "#1a1f3c";
const INDIGO = "#4F46E5";
const INDIGO_LT = "#818cf8";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";

export default function PostACase({ onNavigate }) {
  useConfigVersion(); // re-render with the Admin prices once platform_config loads
  const { t, i18n } = useTranslation();
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 760 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => { trackLandingView("post_a_case"); }, []);

  usePageSeo({
    title: "Post your first ABA case free | ABAcases",
    description: "Post your first ABA case free on ABAcases. No credit card. Works alongside Indeed or your ATS.",
    canonical: "/post-a-case",
    noindex: true,
  });

  // Same free-case count and end date the backend enforces (no hardcoded date).
  const promo = getStarterPromoDates();
  const pricing = getAgencyPricing(i18n.language);
  const params = {
    count: pricing.promoCount,
    endDate: pricing.endDate,
    startDate: pricing.startDate,
    starterMo: pricing.starterMo,
    starterIncluded: pricing.starterIncluded,
    proCases: pricing.proCases,
  };

  const goPostCase = (position) => {
    track(A.AGENCY_SIGNUP_CLICK, {
      supabaseMeta: landingMeta("post_a_case", { cta_position: position }),
      ga4Params: ctaMeta({ pagePath: "/post-a-case/", pageType: "agency", ctaLabel: "Post your first case free", targetPath: "/auth", userLanguage: i18n.language, audience: "agency" }),
    });
    setSignupIntent("agency");
    onNavigate("auth"); // App sets authInitialMode="signup" for this hand-off
  };

  const sectionPad = isMobile ? "36px 18px" : "48px 48px";
  const maxW = 900;

  const Cta = ({ position }) => (
    <button onClick={() => goPostCase(position)} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: isMobile ? "13px 24px" : "15px 30px", background: `linear-gradient(135deg, ${INDIGO} 0%, #6366F1 100%)`, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 24px rgba(79,70,229,0.4)" }}>
      <BriefcaseIcon size={16} color="#fff" /> {promo.isPromoActive ? t("postACase.cta") : t("postACase.ctaPaid")}
    </button>
  );

  const IconBox = ({ Icon }) => (
    <div style={{ width: 42, height: 42, borderRadius: 11, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Icon size={19} color={INDIGO} />
    </div>
  );

  const steps = [
    { Icon: BriefcaseIcon, title: t("postACase.step1Title"), body: t("postACase.step1Body") },
    { Icon: BellIcon, title: t("postACase.step2Title"), body: t("postACase.step2Body") },
    { Icon: UsersIcon, title: t("postACase.step3Title"), body: t("postACase.step3Body") },
  ];
  const trust = [
    { Icon: BuildingIcon, title: t("postACase.trust1Title"), body: t("postACase.trust1Body") },
    { Icon: ShieldIcon, title: t("postACase.trust2Title"), body: t("postACase.trust2Body") },
    { Icon: LockIcon, title: t("postACase.trust3Title"), body: t("postACase.trust3Body") },
  ];

  return (
    <div style={{ flex: 1, overflowY: "auto", background: "#fff", fontFamily: T.fontBody }}>

      {/* Hero: repeat the email offer */}
      <header style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: isMobile ? "36px 18px 40px" : "56px 48px 56px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -120, right: "10%", width: 420, height: 420, borderRadius: "50%", background: "rgba(79,70,229,0.16)", filter: "blur(90px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: maxW, margin: "0 auto" }}>
          <span style={{ display: "inline-block", fontSize: 11.5, fontWeight: 800, color: INDIGO_LT, background: "rgba(255,255,255,0.1)", borderRadius: 7, padding: "4px 10px", letterSpacing: "0.02em", marginBottom: 14 }}>{t("postACase.badge")}</span>
          <h1 style={{ fontSize: isMobile ? 29 : 42, fontWeight: 900, color: "#fff", margin: "0 0 12px", lineHeight: 1.12, letterSpacing: "-0.025em", maxWidth: 760 }}>
            {t("postACase.heroTitle")}
          </h1>
          <p style={{ fontSize: isMobile ? 16 : 19, fontWeight: 600, color: "#e2e8f0", lineHeight: 1.4, maxWidth: 700, margin: "0 0 10px" }}>
            {promo.isPromoActive ? t("postACase.heroOffer", params) : t("postACase.heroOfferNoPromo", params)}
          </p>
          <p style={{ fontSize: isMobile ? 15 : 16.5, color: "#cbd5e1", lineHeight: 1.6, maxWidth: 640, margin: "0 0 26px" }}>
            {t("postACase.heroBody")}
          </p>
          <Cta position="hero" />
          <div style={{ marginTop: 22 }}>
            <ProofChips dark items={agencyChips(t)} />
          </div>
        </div>
      </header>

      {/* How it works */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 28px", letterSpacing: "-0.02em", textAlign: "center" }}>{t("postACase.howTitle")}</h2>
          <FlowSteps isMobile={isMobile} steps={steps.map((st) => ({ Icon: st.Icon, title: st.title, sub: st.body }))} />
        </div>
      </section>

      {/* We help fill your case */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 28px", letterSpacing: "-0.02em", textAlign: "center" }}>{t("agencyVisuals.promoTitle")}</h2>
          <CasePromotion isMobile={isMobile} />
        </div>
      </section>

      {/* Differentials (3 of 6) */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 24px", letterSpacing: "-0.02em", textAlign: "center" }}>{t("agencyVisuals.diffTitle")}</h2>
          <DifferentialGrid sample isMobile={isMobile} only={["map", "fit", "verified"]} />
        </div>
      </section>

      {/* Alongside Indeed / ATS */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto", display: "flex", gap: 16, alignItems: "flex-start" }}>
          <IconBox Icon={CheckIcon} />
          <div>
            <h2 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 900, color: NAVY, margin: "0 0 8px", letterSpacing: "-0.02em" }}>{t("postACase.alongsideTitle")}</h2>
            <p style={{ fontSize: 15, color: TEXT_MID, lineHeight: 1.7, margin: 0 }}>{t("postACase.alongsideBody")}</p>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 24px", letterSpacing: "-0.02em", textAlign: "center" }}>{t("postACase.trustTitle")}</h2>
          <IconTiles columns={3} isMobile={isMobile} mobileColumns={1} items={trust.map((c) => ({ Icon: c.Icon, title: c.title, sub: c.body }))} />
          <div style={{ marginTop: 32 }}><CasePreview isMobile={isMobile} /></div>
        </div>
      </section>

      {/* Outside Florida (after value and trust, so a Florida agency from the email sees those first) */}
      <section style={{ padding: isMobile ? "0 16px 8px" : "0 48px 12px" }}>
        <OutsideFloridaNote isMobile={isMobile} />
      </section>

      {/* Founding Partner */}
      <section style={{ padding: sectionPad }}>
        <PartnerCard isMobile={isMobile} onApply={() => setPartnerOpen(true)} />
      </section>

      {/* Founder */}
      <section style={{ padding: sectionPad, borderTop: `1px solid ${BORDER}` }}>
        <FounderCard isMobile={isMobile} onMessage={() => setContactOpen(true)} />
      </section>

      {contactOpen && <ContactModal onClose={() => setContactOpen(false)} />}
      {partnerOpen && <ContactModal initialTopic="Partnership" initialMessage={t("contactModal.partnerPrefill")} onClose={() => setPartnerOpen(false)} />}
      <PublicFooter />
      <StickyMobileCta label={promo.isPromoActive ? t("agencyPricing.cta") : t("agencyPricing.ctaPaid")} onClick={() => goPostCase("sticky")} ctaId="agency" />
    </div>
  );
}
